<template lang="pug">
.projects-area(v-if="projectsWithGeoJson")
  template(v-if="hasDataInMap")
    template(v-for="(geoJson, index) in projectInMapList")
      div(:key="`project-geojson-${index}`")
        ProjectArea(
          ref="projectAreas"
          :id="`project-area-${getProjectDataByGeoJson(geoJson).code}`"
          :map="map"
          :geoJson="geoJson"
          :projectInfo="getProjectPropertiesByCode(getProjectDataByGeoJson(geoJson).code)"
          :zoomProjectCode="zoomProjectCode"
          @showProjectDetail="$emit('showProjectDetail', $event)"
        )
  SubProjectsArea(
    v-if="subProjectGeoJson && interfaceState.shapes"
    :geoJson="subProjectGeoJson"
    @change-location="changeLocation($event)"
  )

</template>

<script>
import bouncing from 'leaflet.smooth_marker_bouncing'
import { findRealParent } from 'vue2-leaflet'
import { mapActions, mapGetters } from 'vuex'
import { getDefaultLayerStyle, buildSelectedIcon, buildSmallIcon } from '@/utils/map-utils.js'
import { getData } from '@/utils/object'

let firstCenter = false

export default {
  props: {
    showProjectDetailProp: {
      type: Boolean,
      default: false
    }
  },
  components: {
    ProjectArea: _ => import('@/components/map/solucion-definitiva/mapa/ProjectArea'),
    SubProjectsArea: _ => import('@/components/map/solucion-definitiva/mapa/SubProjectsArea')
  },
  data () {
    return {
      projectInMapList: [],
      lastProjectSelected: {},
      lastHoverProjectEvent: null,
      projectsWithGeoJson: null,
      subProjectGeoJson: null,
      isMounted: false
    }
  },
  mounted () {
    if (!bouncing) console.warn('Not bouncing library detected')
    this.isMounted = true
    this.getDataByPromise(this.dataWithShapes)
  },
  methods: {

    ...mapActions([
      'showControl',
      'hideControl',
      'showLoader',
      'hideLoader',
      'setProjectInfoAndUpdateRoute'
    ]),

    getProjectDataByGeoJson (geoJson) {
      return getData(getData(geoJson.features.find(f => f.properties.parent)).properties)
    },

    getProjectPropertiesByCode (code) {
      return this.projectsWithGeoJson ? this.projectsWithGeoJson.projects.filter(c => c && c.code).find(p => p.code === code) : {}
    },

    getProjectAreaElementByCode (code) {
      return this.$refs.projectAreas ? this.$refs.projectAreas.find(projectArea => projectArea.$el.id === `project-area-${code}`) : null
    },

    showParentsShapes () {
      // Add Only Parents Shape
      if (this.projectsWithGeoJson && this.projectsWithGeoJson.projects && this.projectsWithGeoJson.projects.length) {
        this.projectInMapList = this.interfaceState.shapes ? this.projectsWithGeoJson.projects.filter(p => !!(p && p.geoJson)).map(p => p.geoJson) : []
        setTimeout(() => {
          this.$emit('allProjectAreasAdded')
        }, 500)
      }
    },

    removeSubProjectMarkers () {
      if (this.lastProjectSelected.layer && this.lastProjectSelected.layer['_map']) {
        this.lastProjectSelected.element.setIcon(buildSmallIcon(this.lastProjectSelected))
        this.lastProjectSelected.layer.setStyle(getDefaultLayerStyle(this.lastProjectSelected.feature))
      }
      this.lastProjectSelected = {}
    },

    setMarkerInCenter (layer, zoom) {
      let layerInfo = layer.getBounds()
      const customZoom = zoom || (this.map.getBoundsZoom(layerInfo, true) - 2)
      const self = this
      setTimeout(() => {
        let center = self.projectAreaMapData.layer.getCenter()
        if (self.projectAreaMapData.layer) self.map.setView(center, customZoom)
      }, 500)
    },

    /**
     * This method restores the previous selected icon (if exists)
     */
    restoreLastUsedAndAssignCurrent ({ feature, layer, marker, projectInfo }) {
      if (projectInfo) {
        if (projectInfo.number !== this.lastProjectSelected.number) {
          this.removeSubProjectMarkers()
        }
        const self = this
        setTimeout(() => {
          const { layer, marker } = self.projectAreaMapData
          if (layer && marker && (projectInfo.number !== self.lastProjectSelected.number)) {
            const icon = buildSelectedIcon(projectInfo)
            marker.setIcon(icon)
            layer.setStyle({
              weight: 10,
              color: projectInfo.color,
              dashArray: 'continuo',
              opacity: 1
            })
            self.lastProjectSelected = {
              element: marker,
              number: projectInfo.number,
              color: projectInfo.color,
              code: projectInfo.code,
              feature,
              layer
            }
          }
        })
      } else {
        this.removeSubProjectMarkers()
      }
    },

    viewDetail ({ feature, layer, marker, projectInfo }) {
      this.setProjectInfoAndUpdateRoute({ projectInfo })

      this.$emit('showProjectDetail', true)

      this.restoreLastUsedAndAssignCurrent({ feature, layer, marker, projectInfo })

      if (!firstCenter) {
        this.setMarkerInCenter(layer, feature.properties.zoom)
        firstCenter = true
      }

      this.setMarkerInCenter(layer, feature.properties.zoom)

      setTimeout(() => {
        if (marker && marker.options.icon && marker._icon) marker.bounce(1)
      }, 500)

      this.subProjectGeoJson = projectInfo.geoJson
    },

    changeLocation ({ location }) {
      this.descriptionIndex = this.getProjectInfo.componentes.findIndex(c => c.name === location.name)
      this.descriptionIndex = this.descriptionIndex === -1 ? 0 : this.descriptionIndex
      this.$emit('showProjectDetail', true)
    },

    markBorder (projectData) {
      if (this.lastHoverProjectEvent) this.unMarkBorder()
      const hoverDataEvent = this.getProjectInfoAreaMapData(projectData)
      if (!hoverDataEvent.feature) {
        console.warn('Marcando border de proyecto no dibujado', hoverDataEvent)
        return
      }

      // this is the case when the project is selected, we dont mess with it
      if (projectData.code === this.lastProjectSelected.number) return

      const layer = hoverDataEvent.layer
      const feature = hoverDataEvent.feature
      this.lastHoverProjectEvent = hoverDataEvent

      layer.setStyle({
        weight: 6,
        color: feature.properties.color,
        dashArray: 'continuo',
        opacity: 1
      })
    },

    unMarkBorder () {
      if (!this.lastHoverProjectEvent.feature) {
        console.warn('Desmarcando border de proyecto no dibujado', this.lastHoverProjectEvent)
        return
      }
      // this is the case when the project is selected, we dont mess with it
      if (this.lastHoverProjectEvent && this.lastHoverProjectEvent.projectInfo && this.lastHoverProjectEvent.projectInfo.number === this.lastProjectSelected.number) return

      const layer = this.lastHoverProjectEvent.layer
      const feature = this.lastHoverProjectEvent.feature
      layer.setStyle(getDefaultLayerStyle(feature))
    },

    getDataByPromise (promise) {
      if (promise.then) {
        const self = this
        this.showLoader({ hideOverlay: true })
        setTimeout(_ => {
          this.projectsWithGeoJson = null
        })
        promise.then(data => {
          setTimeout(_ => {
            this.projectsWithGeoJson = data
            setTimeout(_ => {
              self.hideLoader({ hideOverlay: false })
              if (self.projectAreaMapData.projectInfo) {
                self.viewDetail(self.projectAreaMapData)
              }
            }, 500)
          })
        })
      }
    },

    getProjectInfoAreaMapData (project) {
      const $projectArea = this.getProjectAreaElementByCode(project.code)
      if (!$projectArea) {
        console.warn('El objeto $projectArea no existe')
        return {}
      }
      return $projectArea.getProjectAreaMapData()
    }
  },
  computed: {
    ...mapGetters([
      'dataWithShapes',
      'getHoverProject',
      'getProjectInfo',
      'interfaceState',
      'shapesState'
    ]),
    map () {
      if (!this.isMounted) return
      return getData(findRealParent(this.$parent, true)).mapObject
    },
    zoomProjectCode () {
      // zoom inicial
      let zoomProjectCode = null
      if (this.$route.query && this.$route.query.code) {
        const code = this.$route.query.code
        const projectProperties = this.getProjectPropertiesByCode(code)
        if (projectProperties) {
          zoomProjectCode = projectProperties.code
        } else {
          console.warn('Proyecto no encontrado', code)
        }
      }
      return zoomProjectCode
    },
    hasDataInMap () {
      return this.map && this.projectsWithGeoJson && this.projectsWithGeoJson.projects && this.projectsWithGeoJson.projects.length && this.interfaceState.shapes
    },
    projectAreaMapData () {
      if (!this.getProjectInfo) return {}
      return this.getProjectInfoAreaMapData(this.getProjectInfo)
    }
  },
  watch: {
    dataWithShapes (promise) {
      this.getDataByPromise(promise)
    },
    getHoverProject (projectData) {
      if (!this.isMounted) return
      if (projectData && projectData.code) this.markBorder(projectData)
      else if (this.lastHoverProjectEvent) this.unMarkBorder()
    },
    getProjectInfo (val) {
      if (val) this.viewDetail(this.projectAreaMapData)
    },
    projectsWithGeoJson (val) {
      if (val) this.showParentsShapes()
    },
    shapesState (state) {
      if (state) {
        this.showParentsShapes()
        this.showControl('projectList')
      } else {
        this.hideControl('projectList')
      }
    },
    showProjectDetailProp (val) {
      if (!val) {
        this.restoreLastUsedAndAssignCurrent({})
        this.subProjectGeoJson = null
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
@import '~@/styles/map/map-scoped'
@import '~@/styles/map/refugios-scoped'

</style>
